import React, { FC, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom';
import Modal from '../../../components/modal/modal.component';
import { GreenTick, VideoIcon } from '../../../assets';
import { useTranslation } from 'react-i18next';
import classes from '../styles.module.scss'
import { createCn } from '../../../helpers/utils'
import { useTasks } from '../../../contexts/tasks.context';
import { TaskItem, TaskPriority, TaskStatus } from '../../../services/api.service';
import { toast } from 'react-toastify';
import globalClasses from '../../styles.module.scss'
import { PRIORITY_ORDER } from './column.component';

const cn = createCn("tasks", classes);

const STATUS_ORDER: TaskStatus[] = ['todo', 'in_progress', 'review', 'done'];

const STATUS_KEY: Record<TaskStatus, string> = {
  todo: 'tasks-board.col-todo',
  in_progress: 'tasks-board.col-in-progress',
  review: 'tasks-board.col-review',
  done: 'tasks-board.col-done',
};

interface IProps {
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  task: TaskItem;
  colTitle: string;
}

function toDateInputValue(dueDate?: string | null): string {
  if (!dueDate) return '';
  const date = new Date(dueDate);
  if (Number.isNaN(date.getTime())) return '';
  const pad = (n: number) => n.toString().padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

const TaskModal: FC<IProps> = ({ open, setOpen, task, colTitle }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { updateTask, deleteTask } = useTasks();
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const [form, setForm] = useState({
    title: task.title,
    description: task.description || '',
    dueDate: toDateInputValue(task.due_date),
    priority: task.priority as TaskPriority,
    status: task.status as TaskStatus,
  });

  // Синхронизация формы при открытии (задача могла обновиться после drag&drop).
  useEffect(() => {
    if (open) {
      setForm({
        title: task.title,
        description: task.description || '',
        dueDate: toDateInputValue(task.due_date),
        priority: task.priority,
        status: task.status,
      });
      setConfirmDelete(false);
    }
  }, [open, task]);

  const handleSave = async () => {
    if (!form.title.trim() || saving) return;
    setSaving(true);
    try {
      await updateTask(task.id, {
        title: form.title.trim(),
        description: form.description.trim() || undefined,
        due_date: form.dueDate ? `${form.dueDate}T00:00:00` : null,
        priority: form.priority,
        status: form.status,
      });
      toast(<div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
        <GreenTick />
        {t('tasks-board.task-updated')}
      </div>);
      setOpen(false);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : t('tasks-board.update-error'));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (saving) return;
    setSaving(true);
    try {
      await deleteTask(task.id);
      toast(<div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
        <GreenTick />
        {t('tasks-board.task-deleted')}
      </div>);
      setOpen(false);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : t('tasks-board.delete-error'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      show={open}
      handleClose={() => setOpen(false)}
      style={{ padding: 0, borderRadius: '1.5rem' }}
    >
      <div className={cn("task__modal")}>
        <div className={cn("task__modal-header")}>
          <span>{colTitle}</span>
          {task.meeting_id && (
            <button
              type="button"
              className={cn("task__modal-meeting-link")}
              onClick={() => {
                setOpen(false);
                navigate(`/files/${task.meeting_id}`);
              }}
            >
              <VideoIcon />
              {t('tasks-board.from-meeting')}
            </button>
          )}
        </div>

        <div className={cn("task__modal-body")}>
          <div className={cn("task__modal-left")}>
            <div className={cn("task__modal-headings")}>
              <div className={globalClasses['form-group']}>
                <label>{t('tasks.task-title')}</label>
                <input
                  type="text"
                  value={form.title}
                  onChange={(e) => setForm({ ...form, title: e.target.value })}
                  placeholder={t('tasks.task-title-placeholder') || ''}
                />
              </div>
              <div className={globalClasses['form-group']}>
                <label>{t('tasks.description')}</label>
                <textarea
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  placeholder={t('tasks.description-placeholder') || ''}
                />
              </div>
            </div>
          </div>

          <div className={cn("task__modal-right")}>
            <div className={globalClasses['form-group']}>
              <label>{t('tasks.due-date')}</label>
              <input
                type="date"
                value={form.dueDate}
                onChange={(e) => setForm({ ...form, dueDate: e.target.value })}
              />
            </div>
            <div className={globalClasses['form-group']}>
              <label>{t('tasks-board.priority')}</label>
              <select
                value={form.priority}
                onChange={(e) => setForm({ ...form, priority: e.target.value as TaskPriority })}
              >
                {PRIORITY_ORDER.map(p => (
                  <option key={p} value={p}>{t(`tasks-board.priority-${p}`)}</option>
                ))}
              </select>
            </div>
            <div className={globalClasses['form-group']}>
              <label>{t('tasks-board.status')}</label>
              <select
                value={form.status}
                onChange={(e) => setForm({ ...form, status: e.target.value as TaskStatus })}
              >
                {STATUS_ORDER.map(s => (
                  <option key={s} value={s}>{t(STATUS_KEY[s])}</option>
                ))}
              </select>
            </div>
          </div>
        </div>

        <div className={cn("task__modal-footer")}>
          {confirmDelete ? (
            <>
              <span className={cn("task__modal-confirm-text")}>
                {t('tasks-board.confirm-delete-task')}
              </span>
              <button
                type="button"
                className={cn("task__modal-delete")}
                onClick={() => { void handleDelete() }}
                disabled={saving}
              >
                {t('action.delete')}
              </button>
              <button type="button" onClick={() => setConfirmDelete(false)} disabled={saving}>
                {t('action.cancel')}
              </button>
            </>
          ) : (
            <>
              <button
                type="button"
                className={cn("task__modal-delete")}
                onClick={() => setConfirmDelete(true)}
                disabled={saving}
              >
                {t('action.delete')}
              </button>
              <button
                type="button"
                className={cn("task__modal-save")}
                onClick={() => { void handleSave() }}
                disabled={!form.title.trim() || saving}
              >
                {t('action.save')}
              </button>
            </>
          )}
        </div>
      </div>
    </Modal>
  )
}

export default TaskModal
